
import { navigateToWrite } from '@/utils/navigationUtils';
import { Vue, Component } from 'vue-property-decorator';
import {
  getCompanyInfo,
  getCompanyDetail,
  incrementCompanyView,
  addCompanyLog,
} from '@/api/company';
import { ICompanyLog } from '@/types/company';
import { UserModule } from '@/store/user';
import { getUserInfo } from '@/api/user';

@Component
export default class InvestCompanyDetail extends Vue {
  private coUid = '';

  private userUid = '';

  private coData: any = null;

  private uData: any = [];

  private frCname = '';

  private toCname = '';

  private sort = '';

  private currentIndex = 0;

  private containerHeight = 0;

  private t2Height = 0;

  private resizeObserver: any = null;

  get imagePath() {
    return `${process.env.VUE_APP_COMMON_API}/attached-file`;
  }

  // [추가] 파일을 ord 순서대로 정렬하여 반환하는 Computed Property
  get sortedFiles() {
    if (!this.coData || !this.coData.files) return [];

    // 원본 배열을 복사([...])한 후 ord 기준으로 오름차순 정렬
    return [...this.coData.files].sort(
      (a: any, b: any) => Number(a.ord) - Number(b.ord),
    );
  }

  get isIrMatchActive(): boolean {
    return this.uData.coDept === 'S';
  }

  get youtubeList() {
    // 데이터가 없으면 빈 배열 반환
    if (!this.coData || !this.coData.youtubeLink) {
      return [];
    }

    // \n으로 자르고, 혹시 모를 공백 제거 후 빈 줄은 제외
    return this.coData.youtubeLink
      .split('\n')
      .map((url: string) => url.trim())
      .filter((url: string) => url.length > 0);
  }

  // [추가] 이미지가 로드되었을 때 높이 계산 (처음 로딩 시 필수)
  private onImageLoad(index: number) {
    // 현재 보고 있는 이미지이거나, 첫 번째 이미지라면 높이 업데이트
    if (index === this.currentIndex) {
      this.updateHeight();
    }
  }

  // [추가] 현재 인덱스의 이미지 높이로 컨테이너 높이 설정
  private updateHeight() {
    this.$nextTick(() => {
      const images = this.$refs.slideImages as HTMLImageElement[];
      if (images && images[this.currentIndex]) {
        // 현재 보여지는 이미지의 실제 높이(렌더링된 높이)를 가져옴
        this.containerHeight = images[this.currentIndex].offsetHeight;
      }
    });
  }

  // [수정] 슬라이드 이동 시 높이 업데이트 호출
  private prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    } else {
      this.currentIndex = this.sortedFiles.length - 1;
    }
    this.updateHeight(); // 이동 후 높이 재계산
  }

  // [수정] 슬라이드 이동 시 높이 업데이트 호출
  private nextSlide() {
    if (this.currentIndex < this.sortedFiles.length - 1) {
      this.currentIndex += 1;
    } else {
      this.currentIndex = 0;
    }
    this.updateHeight(); // 이동 후 높이 재계산
  }

  private getExternalLink(url: string) {
    if (!url) return '#'; // URL이 없으면 샵(#) 반환

    // 이미 http:// 나 https:// 로 시작하면 그대로 반환
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // 없으면 http:// 를 붙여서 반환
    return `http://${url}`;
  }

  async created() {
    // 비로그인 접근 제한
    if (!UserModule.token) {
      alert('로그인이 필요한 페이지입니다.');
      this.$router.push({ name: 'Login' });
      return;
    }
    // 회사 설정
    if (this.$route.params.investCompanyUid) {
      this.coUid = this.$route.params.investCompanyUid as string;
      // 조회수 증가 함수
      this.incrementView(this.coUid);
    } else {
      // console.log('회사 라우트 파라미터가 없습니다.');
    }

    // 1. 회사 상세 정보 가져오기
    const res = await getCompanyDetail(this.coUid);
    this.coData = res.data;
    this.userUid = res.data.userUid;
    // console.log('coData', this.coData);
    this.toCname = this.coData.coName || '';
    let members = [];
    if (this.coData.texts) {
      members = this.coData.texts
        .filter((t: any) => t.part === 'coMember')
        .map((t: any) => t.content);
    }
    this.$set(this.coData, 'coMember', members);

    if (UserModule.token) {
      // 2. 사용자 정보 가져오기
      const uRes = await getUserInfo();
      const { uid } = uRes.data;

      if (uid) {
        UserModule.setUserUid(uid);
        const ucRes = await getCompanyInfo(uid);
        this.uData = ucRes.data;
        this.frCname = this.uData.coName || '';
      }
    }

    // 데이터 로딩 후 DOM이 그려지면 초기 높이를 맞춤
    this.$nextTick(() => {
      this.syncT2Height();
    });

    // 로그 전송 (sendLog 사용)
    this.sendLog('상세보기');
  }

  async mounted() {
    this.initT2Observer();
  }

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // t2의 높이를 t1에 즉시 동기화하는 함수
  private syncT2Height() {
    const t2Element = document.getElementById('t2');
    if (t2Element) {
      this.t2Height = t2Element.offsetHeight;
    }
  }

  private initT2Observer() {
    this.$nextTick(() => {
      const t2Element = document.getElementById('t2');
      if (t2Element) {
        // 시작하자마자 한 번 맞춤
        this.syncT2Height();

        this.resizeObserver = new (window as any).ResizeObserver((entries: any[]) => {
          // requestAnimationFrame을 사용하면 브라우저 렌더링 주기와 맞춰 더 부드럽게 업데이트됩니다.
          window.requestAnimationFrame(() => {
            this.syncT2Height();
          });
        });
        this.resizeObserver.observe(t2Element);
      }
    });
  }

  // 조회수 증가 함수
  private async incrementView(uid: string) {
    try {
      await incrementCompanyView(uid);
    } catch (error) {
      console.error(`회사 UID: ${uid} 조회수 증가 요청 실패:`, error);
    }
  }

  // 로그 sort
  public calculateSort() {
    if (this.coData?.coDept && this.uData?.coDept) {
      this.sort = `${this.uData.coDept}${this.coData.coDept}`;
    }
  }

  // 로그 추가 함수
  private async logCompanyAction(data: ICompanyLog) {
    try {
      await addCompanyLog(data);
    } catch (error) {
      console.error('로그 추가 실패:', error);
    }
  }

  // 로그 데이터 생성
  private sendLog(part: string) {
    // 3. 두 데이터 모두 확보 후 sort 계산
    this.calculateSort();
    const logData: ICompanyLog = {
      part,
      frCname: this.frCname,
      toCname: this.toCname,
      sort: this.sort,
    };
    this.logCompanyAction(logData);
  }

  private navigateToWritePage() {
    navigateToWrite(this.$router, this.$route, this.userUid);
    this.sendLog('IR 매칭');
  }

  private getThumnail(inputValue: string) {
    if (inputValue.startsWith('http')) return inputValue;
    return `${this.imagePath}/${inputValue}`;
  }
}
